import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, Globe, AlertTriangle, HelpCircle, FileWarning, MessageCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { api } from '../lib/api';
import { PageHeader, Loading, EmptyState } from '../components/Shared';

export default function WhoDoICall() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [activeCat, setActiveCat] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/directory').then((r) => setItems(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const allCategories = [...new Set(items.map((i) => i.category))];
  const searched = items.filter((i) => !search || `${i.title} ${i.description} ${i.category}`.toLowerCase().includes(search.toLowerCase()));
  const filtered = activeCat === 'all' ? searched : searched.filter((i) => i.category === activeCat);
  const groupedCats = [...new Set(filtered.map((i) => i.category))];

  return (
    <div>
      <PageHeader kicker="RESIDENT DIRECTORY" title="Who Do I Call?" subtitle="The right number, the right department, the first time. A plain-language directory for Ward 8 residents." />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        {/* Emergency banner */}
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex gap-3" data-testid="whocall-emergency-banner">
          <AlertTriangle className="h-5 w-5 text-red-700 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-red-900 font-bold">For any police, fire, or medical emergency, call 911 immediately.</p>
            <a href="tel:911" className="inline-flex items-center gap-1.5 mt-1.5 text-sm font-black text-red-800 underline underline-offset-4" data-testid="whocall-911-link"><Phone className="h-4 w-4" /> Call 911</a>
          </div>
        </div>

        {/* Search */}
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search: potholes, hydro, by-law, health…" className="h-12 bg-white" data-testid="directory-search-input" />

        {/* Category chips — thumb-friendly, horizontal scroll on mobile */}
        <div className="flex gap-2 overflow-x-auto py-4 -mx-4 px-4 sm:mx-0 sm:px-0 sm:flex-wrap" data-testid="directory-category-chips">
          <button onClick={() => setActiveCat('all')} className={`shrink-0 h-11 px-4 rounded-full text-sm font-bold border transition-colors duration-150 ${activeCat === 'all' ? 'bg-[#1a2744] text-white border-[#1a2744]' : 'bg-white text-[#1a2744] border-slate-300 hover:border-[#1a2744]'}`} data-testid="directory-chip-all">All</button>
          {allCategories.map((cat) => (
            <button key={cat} onClick={() => setActiveCat(cat)} className={`shrink-0 h-11 px-4 rounded-full text-sm font-bold border transition-colors duration-150 ${activeCat === cat ? 'bg-[#1a2744] text-white border-[#1a2744]' : 'bg-white text-[#1a2744] border-slate-300 hover:border-[#1a2744]'}`} data-testid={`directory-chip-${cat.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>{cat}</button>
          ))}
        </div>

        {loading ? <Loading /> : (
          <>
            {groupedCats.map((cat) => (
              <div key={cat} className="mb-8">
                <h2 className="text-xs font-bold uppercase tracking-widest text-[#C9A84C] mb-3">{cat}</h2>
                <div className="space-y-3">
                  {filtered.filter((i) => i.category === cat).map((item) => (
                    <Card key={item.id} className="card-lift" data-testid="directory-item">
                      <CardContent className="p-5">
                        <p className="font-serif font-bold text-[#1a2744]">{item.title}</p>
                        {item.description && <p className="text-sm text-slate-600 mt-1 leading-relaxed">{item.description}</p>}
                        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:gap-x-5 mt-3">
                          {item.phone && (
                            <Button asChild variant="outline" className="h-11 justify-start border-[#1a2744] text-[#1a2744] font-bold btn-press sm:border-0 sm:h-auto sm:p-0 sm:justify-center" data-testid="directory-phone-link">
                              <a href={`tel:${item.phone.replace(/[^0-9+]/g, '')}`}><Phone className="h-4 w-4 mr-1.5 text-[#C9A84C]" /> {item.phone}</a>
                            </Button>
                          )}
                          {item.email && <a href={`mailto:${item.email}`} className="flex items-center gap-1.5 text-sm font-bold text-[#1a2744] min-h-11 sm:min-h-0" data-testid="directory-email-link"><Mail className="h-4 w-4 text-[#C9A84C]" /> {item.email}</a>}
                          {item.url && <a href={item.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-sm font-bold text-[#1a2744] underline decoration-[#C9A84C] decoration-2 underline-offset-4 min-h-11 sm:min-h-0" data-testid="directory-website-link"><Globe className="h-4 w-4 text-[#C9A84C]" /> Website</a>}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
            {filtered.length === 0 && <EmptyState title="No matches" subtitle="Try a different word, or use the card below if you're not sure where to start." />}
          </>
        )}

        {/* Not sure where to start */}
        <Card className="border-[#C9A84C] bg-[#1a2744]" data-testid="whocall-not-sure-card">
          <CardContent className="p-6 sm:p-8">
            <HelpCircle className="h-8 w-8 text-[#C9A84C] mb-2" />
            <h2 className="font-serif font-black text-white text-xl">Not sure where to start?</h2>
            <p className="text-slate-300 mt-2 leading-relaxed text-sm sm:text-base">Report your issue through the Ward 8 Public Issue Tracker App (PITA). You'll get a tracking number, help writing it in proper municipal language, and a pointer to the right official City contact path.</p>
            <div className="flex flex-col sm:flex-row gap-3 mt-5">
              <Button asChild className="h-12 bg-[#C9A84C] hover:bg-[#b8973f] text-[#1a2744] font-bold btn-press" data-testid="whocall-report-cta">
                <Link to="/issue-tracker/report"><FileWarning className="h-4 w-4 mr-1.5" /> Report an Issue</Link>
              </Button>
              <Button asChild variant="outline" className="h-12 border-white/40 text-white hover:bg-white/10 font-bold btn-press bg-transparent" data-testid="whocall-tracker-cta">
                <Link to="/issue-tracker"><MessageCircle className="h-4 w-4 mr-1.5" /> Visit the Issue Tracker</Link>
              </Button>
            </div>
          </CardContent>
        </Card>

        <p className="text-xs text-slate-500 mt-6 leading-relaxed" data-testid="whocall-disclaimer">This directory is maintained by the campaign as a public service. Numbers and links point to official services. Always confirm details on the official website of each organization.</p>
      </div>
    </div>
  );
}
